import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, Search } from 'lucide-react';
import { COUNTRIES, COUNTRY_CODE_OPTIONS } from '../../data/countryCodes';
import useAnchoredMenu from './useAnchoredMenu';
import './CountryCodeSelect.css';

// The control is narrow (~110px); the panel needs room for country names.
const MENU_MIN_WIDTH = 260;

// One dial code can belong to several countries (+1 → US, CA, PR…). Search has
// to match all of them, so build a search index keyed by the dial code that is
// actually stored.
const SEARCH_INDEX = COUNTRY_CODE_OPTIONS.map((opt) => {
  const sharing = COUNTRIES.filter((c) => c.dial === opt.value);
  return {
    value: opt.value,
    label: opt.label,
    name: sharing[0]?.name || '',
    // keywords already carry name, ISO code, dial code, cities and alt names
    haystack: sharing.map((c) => c.keywords).join(' '),
  };
});

/**
 * Country dial-code picker with type-to-search.
 *
 * A native <select> can't be searched, and the list is 200+ entries long — so
 * this renders the same visual as FormSelect but opens a filterable panel.
 */
const CountryCodeSelect = ({ label, name, value, onChange, disabled }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [highlight, setHighlight] = useState(0);
  const wrapRef = useRef(null);
  const anchorRef = useRef(null);
  const menuRef = useRef(null);
  const searchRef = useRef(null);
  const listRef = useRef(null);
  const menuStyle = useAnchoredMenu(open && !disabled, anchorRef, MENU_MIN_WIDTH);

  const selected = SEARCH_INDEX.find((c) => c.value === value);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return SEARCH_INDEX;
    return SEARCH_INDEX.filter((c) => c.haystack.includes(q));
  }, [query]);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => {
      // The panel is portalled to document.body — it is outside wrapRef.
      if (wrapRef.current?.contains(e.target)) return;
      if (menuRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  useEffect(() => {
    if (open) {
      setQuery('');
      setHighlight(0);
      // focus after the panel paints, otherwise the caret lands nowhere
      const t = setTimeout(() => searchRef.current?.focus(), 0);
      return () => clearTimeout(t);
    }
  }, [open]);

  // keep the highlighted row in view while arrowing through 200+ options
  useEffect(() => {
    if (!open || !listRef.current) return;
    const el = listRef.current.children[highlight];
    if (el) el.scrollIntoView({ block: 'nearest' });
  }, [highlight, open]);

  const pick = (code) => {
    // Mimic a native <select> change event so existing handleChange(e) works.
    onChange({ target: { name, value: code } });
    setOpen(false);
  };

  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight((h) => Math.min(h + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (filtered[highlight]) pick(filtered[highlight].value);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setOpen(false);
    }
  };

  return (
    <div className="form-group" ref={wrapRef}>
      {label && <label className="form-label">{label}</label>}
      <div className={`ccs ${open ? 'ccs--open' : ''}`} ref={anchorRef}>
        <button
          type="button"
          className="ccs__control"
          onClick={() => !disabled && setOpen((o) => !o)}
          disabled={disabled}
        >
          <span className="ccs__value">{selected ? selected.label : value || 'Select'}</span>
          <ChevronDown size={14} className="ccs__chev" />
        </button>

        {open && menuStyle && createPortal(
          <div className="ccs__menu ccs__menu--portal" ref={menuRef} style={menuStyle}>
            <div className="ccs__search">
              <Search size={14} />
              <input
                ref={searchRef}
                value={query}
                onChange={(e) => { setQuery(e.target.value); setHighlight(0); }}
                onKeyDown={onKeyDown}
                placeholder="Search country or code"
              />
            </div>
            <div className="ccs__list" ref={listRef}>
              {filtered.length === 0 && <div className="ccs__empty">No match</div>}
              {filtered.map((c, i) => (
                <button
                  key={c.value}
                  type="button"
                  className={`ccs__option ${c.value === value ? 'ccs__option--active' : ''} ${i === highlight ? 'ccs__option--highlight' : ''}`}
                  onClick={() => pick(c.value)}
                  onMouseEnter={() => setHighlight(i)}
                >
                  <span className="ccs__option-label">{c.label}</span>
                  <span className="ccs__option-name">{c.name}</span>
                </button>
              ))}
            </div>
          </div>,
          document.body
        )}
      </div>
    </div>
  );
};

export default CountryCodeSelect;
